import React, { useState } from 'react';
import { Box, Typography, CircularProgress, useTheme, ToggleButtonGroup, ToggleButton } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { useRevenueTrends } from '../../hooks/useSubscriptions';

const RevenueTrendChart = () => {
    const theme = useTheme();
    const { data: trendData, isLoading, error } = useRevenueTrends();
    const isDark = theme.palette.mode === 'dark';

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                <Typography color="error">Failed to load revenue trends</Typography>
            </Box>
        );
    }

    if (!isLoading && (!trendData?.data || trendData.data.length === 0)) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                <Typography color="text.secondary">No revenue data available for the selected period</Typography>
            </Box>
        );
    }

    const series = [
        {
            name: 'Revenue',
            type: 'line',
            data: trendData?.data?.map(item => item.revenue) || []
        },
        {
            name: 'Transaction Numbers',
            type: 'column',
            data: trendData?.data?.map(item => item.count) || []
        }
    ];

    const options = {
        chart: {
            height: 350,
            type: 'line',
            toolbar: { show: false },
            background: 'transparent'
        },
        stroke: {
            curve: 'smooth',
            width: [3, 0]
        },
        plotOptions: {
            bar: {
                columnWidth: '50%',
                borderRadius: 2
            }
        },
        xaxis: {
            categories: trendData?.data?.map(item => item.date) || [],
            labels: {
                style: { colors: theme.palette.text.secondary },
                rotate: -45,
                formatter: (val) => {
                    if (!val) return '';
                    const date = new Date(val);
                    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                }
            }
        },
        yaxis: [
            {
                title: {
                    text: 'Revenue',
                    style: { color: theme.palette.primary.main }
                },
                labels: {
                    formatter: (val) => `$${(val || 0).toFixed(0)}`,
                    style: { colors: theme.palette.text.secondary }
                }
            },
            {
                opposite: true,
                title: {
                    text: 'Transactions',
                    style: { color: theme.palette.success.main }
                },
                labels: {
                    formatter: (val) => (val || 0).toFixed(0),
                    style: { colors: theme.palette.text.secondary }
                }
            }
        ],
        colors: [theme.palette.primary.main, theme.palette.success.main],
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            shared: true,
            intersect: false,
            y: {
                formatter: (val, { seriesIndex }) => {
                    if (seriesIndex === 0) return `$${(val || 0).toFixed(2)}`;
                    return (val || 0).toFixed(0);
                }
            }
        },
        grid: {
            borderColor: theme.palette.divider
        },
        legend: {
            labels: { colors: theme.palette.text.primary },
            position: 'top'
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                    <CircularProgress />
                </Box>
            ) : (
                <ReactApexChart 
                    key={`revenue-trend-${trendData?.data?.length || 0}`}
                    options={options} 
                    series={series} 
                    type="line" 
                    height={300} 
                />
            )}
        </Box>
    );
};

export default RevenueTrendChart;
